/**
 * Tenant KB entry editor — add a new KB entry, or edit one you added.
 *
 * The form is built from GET /admin/cryptography-kb/:table/schema, which
 * introspects the real table. That matters: the 11 KB tables have quite
 * different columns and several long ENUM lists, so a hand-written form per
 * table would drift from the schema on the next migration. Here, adding a
 * column to a KB table makes it appear in this form automatically.
 *
 * Rules enforced by the server (mirrored in the UI, not trusted to it):
 *   - Creating always produces a kb_origin='Tenant' row.
 *   - Only Tenant rows can be edited; System rows shipped with the platform
 *     are immutable.
 *   - Writes require the super_user role.
 */

import { useState, useEffect, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiUrl, ApiError } from "../api/client";
import { Modal, FormError } from "./admin-ui";

export interface KbFieldMeta {
  name: string;
  kind: "text" | "number" | "boolean" | "enum" | "ref";
  required: boolean;
  maxLength: number | null;
  options: string[] | null;
  refTable: string | null;
  comment: string | null;
}

interface KbSchemaResponse {
  data: {
    table: string;
    fields: KbFieldMeta[];
    ref_options: Record<string, Array<{ id: string; label: string }>>;
  };
}

/** Turns snake_case column names into something readable. */
function labelFor(field: KbFieldMeta): string {
  const base = field.name.replace(/_id$/, "").replace(/_/g, " ");
  return base.charAt(0).toUpperCase() + base.slice(1);
}

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: "same-origin", ...init });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(
      res.status,
      body?.error?.message ?? `HTTP ${res.status}`,
      body,
    );
  }
  return res.json() as Promise<T>;
}

interface Props {
  table: string;
  /** Existing row to edit; omit to create a new entry. */
  row?: Record<string, unknown> | null;
  onClose: () => void;
}

export function KbEntryEditor({ table, row, onClose }: Props) {
  const isEdit = !!row;
  const queryClient = useQueryClient();
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [error, setError] = useState<string | null>(null);

  const schemaQ = useQuery<KbSchemaResponse>({
    queryKey: ["kb-schema", table],
    queryFn: () => jsonFetch<KbSchemaResponse>(apiUrl(`/admin/cryptography-kb/${table}/schema`)),
    staleTime: 10 * 60 * 1000,
  });

  const fields = schemaQ.data?.data.fields ?? [];
  const refOptions = schemaQ.data?.data.ref_options ?? {};

  // Seed the form once the schema is known: existing values when editing,
  // sensible blanks when creating.
  useEffect(() => {
    if (fields.length === 0) return;
    const seed: Record<string, unknown> = {};
    for (const f of fields) {
      const current = row?.[f.name];
      if (current !== undefined && current !== null) {
        seed[f.name] = f.kind === "boolean" ? (current === 1 || current === true) : current;
      } else {
        seed[f.name] = f.kind === "boolean" ? false : "";
      }
    }
    setValues(seed);
  }, [schemaQ.data, row]);

  const save = useMutation({
    mutationFn: async () => {
      // Send only what the user filled in. Empty optional fields go as null so
      // the column is cleared rather than written as an empty string.
      const payload: Record<string, unknown> = {};
      for (const f of fields) {
        const v = values[f.name];
        if (f.kind === "boolean") { payload[f.name] = !!v; continue; }
        if (v === "" || v === undefined) {
          if (!isEdit && f.required) continue; // let the server report it
          payload[f.name] = null;
          continue;
        }
        payload[f.name] = v;
      }
      const url = isEdit
        ? apiUrl(`/admin/cryptography-kb/${table}/${String(row!['id'])}`)
        : apiUrl(`/admin/cryptography-kb/${table}`);
      return jsonFetch(url, {
        method: isEdit ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kb-table", table] });
      queryClient.invalidateQueries({ queryKey: ["kb-schema", table] });
      onClose();
    },
    onError: (e) => setError((e as Error).message),
  });

  const missing = useMemo(
    () => fields.filter((f) => f.required && (values[f.name] === "" || values[f.name] === undefined))
      .map((f) => labelFor(f)),
    [fields, values],
  );

  const set = (name: string, v: unknown) => {
    setValues((prev) => ({ ...prev, [name]: v }));
    setError(null);
  };

  return (
    <Modal
      open
      title={isEdit ? `Edit entry — ${table}` : `Add entry — ${table}`}
      onClose={onClose}
      widthClass="max-w-2xl"
    >
      {schemaQ.isLoading ? (
        <div className="text-sm text-ink-500 italic py-4">Loading field list…</div>
      ) : schemaQ.isError ? (
        <FormError message={(schemaQ.error as Error).message} />
      ) : (
        <form
          onSubmit={(e) => { e.preventDefault(); setError(null); save.mutate(); }}
          className="space-y-4"
        >
          <p className="text-xs text-ink-500">
            {isEdit
              ? "This is one of your own entries, so it can be edited."
              : "New entries are saved as your organisation's own KB content. The knowledge base shipped with the platform is never modified."}
          </p>

          <div className="grid grid-cols-2 gap-3">
            {fields.map((f) => (
              <label key={f.name} className="text-xs text-ink-600 flex flex-col gap-1">
                <span>
                  {labelFor(f)}
                  {f.required && <span className="text-risk-critical"> *</span>}
                </span>

                {f.kind === "boolean" ? (
                  <input
                    type="checkbox"
                    checked={!!values[f.name]}
                    onChange={(e) => set(f.name, e.target.checked)}
                    className="w-4 h-4 mt-1"
                  />
                ) : f.kind === "enum" ? (
                  <select
                    value={String(values[f.name] ?? "")}
                    onChange={(e) => set(f.name, e.target.value)}
                    className="border border-ink-200 rounded px-2 py-1 bg-white text-ink text-sm"
                  >
                    <option value="">— select —</option>
                    {(f.options ?? []).map((o) => (
                      <option key={o} value={o}>{o.replace(/_/g, " ")}</option>
                    ))}
                  </select>
                ) : f.kind === "ref" ? (
                  <select
                    value={String(values[f.name] ?? "")}
                    onChange={(e) => set(f.name, e.target.value)}
                    className="border border-ink-200 rounded px-2 py-1 bg-white text-ink text-sm"
                  >
                    <option value="">— select —</option>
                    {(refOptions[f.refTable ?? ""] ?? []).map((o) => (
                      <option key={o.id} value={o.id}>{o.label}</option>
                    ))}
                  </select>
                ) : f.kind === "number" ? (
                  <input
                    type="number"
                    value={String(values[f.name] ?? "")}
                    onChange={(e) => set(f.name, e.target.value)}
                    className="border border-ink-200 rounded px-2 py-1 bg-white text-ink text-sm"
                  />
                ) : (
                  <input
                    type="text"
                    value={String(values[f.name] ?? "")}
                    onChange={(e) => set(f.name, e.target.value)}
                    maxLength={f.maxLength ?? undefined}
                    className="border border-ink-200 rounded px-2 py-1 bg-white text-ink text-sm"
                  />
                )}

                {f.comment && (
                  <span className="text-[10px] text-ink-400 leading-snug">{f.comment}</span>
                )}
              </label>
            ))}
          </div>

          {error && <FormError message={error} />}

          <div className="flex items-center justify-between pt-2 border-t border-ink-100">
            <button type="button" onClick={onClose} className="text-sm text-ink-500 hover:text-ink">
              Cancel
            </button>
            <div className="flex items-center gap-3">
              {missing.length > 0 && (
                <span className="text-xs text-ink-400">
                  Required: {missing.join(", ")}
                </span>
              )}
              <button
                type="submit"
                disabled={save.isPending || missing.length > 0}
                className="text-sm bg-accent text-white px-4 py-2 rounded hover:bg-accent-hover disabled:opacity-50"
              >
                {save.isPending ? "Saving…" : isEdit ? "Save changes" : "Add entry"}
              </button>
            </div>
          </div>
        </form>
      )}
    </Modal>
  );
}
